/**
 * "Always do this → create rule" dialog (FIN-10, design §3.2).
 *
 * Opened right after a manual categorization. Prefilled as
 * `description contains <text> → <chosen category>` with applyNow checked, so
 * one confirm both saves the rule and back-fills every other matching
 * uncategorized transaction. The user can trim the match text (e.g. from a
 * full noisy description down to "CONTINENTE") before saving.
 */

import { useState } from 'react';
import { FormModal } from '../FormModal';
import { useCreateFinanceRule, type FinanceCategoryDto } from '../../api/finance';
import { financeErrorMessage } from '../../screens/finance/finance-format';
import { CategoryPicker } from './CategoryPicker';
import formStyles from '../FormModal.module.css';

export interface RuleSeed {
  description: string;
  categoryId: string;
}

/** Suggest a concise match token: the first meaningful word of the description. */
function suggestPattern(description: string): string {
  const trimmed = description.trim();
  const firstWord = trimmed.split(/\s+/)[0] ?? trimmed;
  // Prefer the first alphabetic token of length >= 3; else the whole thing.
  return firstWord.length >= 3 ? firstWord : trimmed;
}

export function RuleFromCategorizationDialog({
  seed,
  categories,
  onClose,
}: {
  seed: RuleSeed | null;
  categories: FinanceCategoryDto[];
  onClose: () => void;
}) {
  const create = useCreateFinanceRule();
  const open = !!seed;

  const [pattern, setPattern] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [applyNow, setApplyNow] = useState(true);
  const [seen, setSeen] = useState<string | null>(null);
  const [applied, setApplied] = useState<number | null>(null);

  const key = open ? `${seed.categoryId}:${seed.description}` : null;
  if (open && key !== seen) {
    setSeen(key);
    setPattern(suggestPattern(seed.description));
    setCategoryId(seed.categoryId);
    setApplyNow(true);
    setApplied(null);
    create.reset();
  }
  if (!open && seen !== null) setSeen(null);

  const valid = pattern.trim().length > 0 && !!categoryId;

  const submit = () => {
    if (!valid || create.isPending) return;
    create.mutate(
      {
        criteria: { conditions: [{ field: 'description', type: 'contains', pattern: pattern.trim() }] },
        action: { categoryId },
        applyNow,
      },
      {
        onSuccess: (res) => {
          if (applyNow) {
            setApplied(res.applied ?? 0);
          } else {
            onClose();
          }
        },
      },
    );
  };

  // After an applyNow success show a one-line confirmation, then let the user close.
  if (applied !== null) {
    return (
      <FormModal
        open={open}
        onClose={onClose}
        title="Rule created"
        submitLabel="Done"
        onSubmit={onClose}
      >
        <div className={formStyles.field}>
          <span className={formStyles.message}>
            Rule saved. It categorized {applied} matching uncategorized transaction
            {applied === 1 ? '' : 's'} just now and will run on every future import.
          </span>
        </div>
      </FormModal>
    );
  }

  return (
    <FormModal
      open={open}
      onClose={onClose}
      title="Always do this"
      subtitle="Create a rule so future imports categorize these automatically."
      submitLabel={create.isPending ? 'Saving…' : 'Create rule'}
      submitDisabled={!valid || create.isPending}
      onSubmit={submit}
      error={financeErrorMessage(create.error)}
    >
      <div className={formStyles.field}>
        <label className={formStyles.fieldLabel} htmlFor="rule-pattern">
          When description contains
        </label>
        <input
          id="rule-pattern"
          className={formStyles.input}
          value={pattern}
          maxLength={200}
          onChange={(e) => setPattern(e.target.value)}
          placeholder="e.g. CONTINENTE"
        />
        <span className={formStyles.fieldHint}>Case-insensitive substring match.</span>
      </div>

      <div className={formStyles.field}>
        <span className={formStyles.fieldLabel}>Categorize as</span>
        <CategoryPicker
          categories={categories}
          value={categoryId || null}
          placeholder="Select a category…"
          ariaLabel="Categorize as"
          onChange={(catId) => setCategoryId(catId)}
        />
      </div>

      <div className={formStyles.field}>
        <label className={formStyles.fieldLabel} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={applyNow}
            onChange={(e) => setApplyNow(e.target.checked)}
          />
          Apply to existing uncategorized transactions now
        </label>
      </div>
    </FormModal>
  );
}
